import React, { FC, useState } from 'react';
import { Button, Icon, Menu }  from 'semantic-ui-react';
import type { SemanticICONS }  from 'semantic-ui-react';
import { Flexbox, FlexItem, StyledContainer }                    from '@src/views/containers';
import { CorvalUIColor, FlexAlign, FlexJustify, Position, Size } from '@src/types';
import {
    DashSegment,
    FINANCE_ENTITY,
    SidebarEntity,
    SidebarEntityKind,
    SidebarSection,
} from './consultantDashModel';

const KIND_ICONS: Record<SidebarEntityKind, SemanticICONS> = {
    company:    'building outline',
    consultant: 'user outline',
    executive:  'chart line',
    reports:    'chart bar',
    finance:    'dollar sign',
};

interface SidebarSectionAction {
    sectionKind: SidebarEntityKind;
    label: string;
    icon: SemanticICONS;
    onClick: () => void;
}

interface ConsultantDashSidebarProps {
    sections: SidebarSection[];
    activeEntityId: string;
    onSelectEntity: ( entity: SidebarEntity ) => void;
    /** Entities the consultant has chosen to hide from their panel */
    hiddenEntityIds: string[];
    onToggleEntityVisibility: ( entityId: string ) => void;
    isCollapsed: boolean;
    toggleCollapsed: () => void;
    segment: DashSegment;
    /** Optional per-section actions (e.g. Add Consultant under Consultants). */
    sectionActions?: SidebarSectionAction[];
    /** Optional top-level admin controls popup trigger. */
    onOpenAdminControls?: () => void;
}

/**
 * Left panel of the Consultant Dash. Fully customizable: the consultant can
 * enter "customize" mode (sliders icon) and toggle which companies, consultants
 * and executive views appear in their panel. Hidden items only show while
 * customizing, dimmed, so they can be re-enabled later.
 */
const ConsultantDashSidebar: FC<ConsultantDashSidebarProps> = ( {
    sections,
    activeEntityId,
    onSelectEntity,
    hiddenEntityIds,
    onToggleEntityVisibility,
    isCollapsed,
    toggleCollapsed,
    segment,
    sectionActions = [],
    onOpenAdminControls,
} ) => {

    const [ customizing, setCustomizing ] = useState( false );
    // Labeled sections the consultant has expanded; everything starts collapsed.
    const [ expandedSections, setExpandedSections ] = useState<string[]>( [] );

    const toggleSection = ( sectionKey: string ) =>
        setExpandedSections( prev => prev.includes( sectionKey )
            ? prev.filter( k => k !== sectionKey )
            : [ ...prev, sectionKey ] );

    const isHidden = ( entityId: string ) => hiddenEntityIds.includes( entityId );

    const actionsForSection = ( kind: SidebarEntityKind ): SidebarSectionAction[] =>
        sectionActions.filter( action => action.sectionKind === kind );

    const renderEntity = ( entity: SidebarEntity ) => {

        const canHide = entity.kind !== 'reports' && entity.kind !== 'finance';
        const hidden  = canHide && isHidden( entity.id );

        // Hidden entities are only rendered while customizing
        if( hidden && !customizing ) return null;

        return (
            <Menu.Item
                key={ entity.id }
                active={ activeEntityId === entity.id }
                className={ `menu-item ${ activeEntityId === entity.id ? 'active' : '' } ${ hidden ? 'is-hidden-entity' : '' }` }
                onClick={ () => { if( !customizing && !hidden ) onSelectEntity( entity ); } }
            >
                <Flexbox justify={ FlexJustify.START } align={ FlexAlign.CENTER } className={ 'menu-item-inner' }>
                    <Icon name={ KIND_ICONS[ entity.kind ] }/>
                    { !isCollapsed && (
                        <>
                            <span className={ 'menu-item-text' }>{ entity.name }</span>
                            { customizing && canHide && (
                                <Icon
                                    name={ ( hidden ? 'eye slash' : 'eye' ) as SemanticICONS }
                                    className={ 'menu-item-visibility-toggle' }
                                    title={ hidden ? 'Show in panel' : 'Hide from panel' }
                                    onClick={ ( e: React.MouseEvent ) => {
                                        e.stopPropagation();
                                        onToggleEntityVisibility( entity.id );
                                    } }
                                />
                            ) }
                        </>
                    ) }
                </Flexbox>
            </Menu.Item>
        );
    };

    const renderSectionAction = ( action: SidebarSectionAction ) => {
        if( isCollapsed ){
            return (
                <button
                    key={ action.label }
                    type={ 'button' }
                    className={ 'consultant-sidebar-action consultant-sidebar-action--icon' }
                    title={ action.label }
                    onClick={ action.onClick }
                >
                    <Icon name={ action.icon }/>
                </button>
            );
        }

        return (
            <button
                key={ action.label }
                type={ 'button' }
                className={ 'consultant-sidebar-action' }
                onClick={ action.onClick }
            >
                <Icon name={ action.icon }/>
                <span>{ action.label }</span>
            </button>
        );
    };

    return (
        <StyledContainer
            className={ 'sidebar-container' }
            bgColor={ CorvalUIColor.WHITE }
            position={ Position.RELATIVE }
        >
            <StyledContainer
                position={ Position.STICKY }
                top={ 0 }
                zIndex={ 200 }
                className={ 'sidebar-header' }
                bgColor={ CorvalUIColor.WHITE }
                borderSize={ [ null, null, Size.SM, null ] }
                borderColor={ CorvalUIColor.DK_SCREEN_25 }
            >
                <Flexbox justify={ FlexJustify.SPACE_BETWEEN } align={ FlexAlign.CENTER } className={ 'sidebar-toggle-container' }>
                    <FlexItem noGrow>
                        { !isCollapsed && <span className={ 'consultant-sidebar-title' }>My Panel</span> }
                    </FlexItem>
                    <FlexItem noGrow>
                        { onOpenAdminControls && !isCollapsed && (
                            <Button
                                icon={ 'setting' as SemanticICONS }
                                size={ 'mini' }
                                basic
                                onClick={ onOpenAdminControls }
                                className={ 'sidebar-admin-btn' }
                                title={ 'Admin controls' }
                            />
                        ) }
                        { !isCollapsed && (
                            <Button
                                icon={ ( customizing ? 'checkmark' : 'sliders horizontal' ) as SemanticICONS }
                                size={ 'mini' }
                                color={ customizing ? 'green' : undefined }
                                basic
                                onClick={ () => setCustomizing( prev => !prev ) }
                                className={ 'sidebar-edit-btn' }
                                title={ customizing ? 'Done customizing' : 'Customize panel' }
                            />
                        ) }
                        <Button
                            icon={ ( isCollapsed ? 'angle right' : 'angle left' ) as SemanticICONS }
                            size={ 'mini' }
                            basic
                            onClick={ toggleCollapsed }
                            className={ 'sidebar-toggle-btn-inner' }
                        />
                    </FlexItem>
                </Flexbox>
            </StyledContainer>

            <StyledContainer className={ 'sidebar-content' }>
                { customizing && !isCollapsed && (
                    <p className={ 'consultant-sidebar-customize-hint' }>
                        Choose what appears in your panel. Hidden items stay available here.
                    </p>
                ) }

                {/* Finance is hard-coded rather than coming from getSidebarSections. */}
                <div className={ 'consultant-sidebar-section' }>
                    <Menu fluid vertical size={ 'small' } secondary className={ 'sidebar-menu' }>
                        { renderEntity( FINANCE_ENTITY ) }
                    </Menu>
                </div>

                { sections.map( section => {

                    const visibleCount = section.entities.filter( e => e.kind === 'reports' || !isHidden( e.id ) ).length;
                    const actions = actionsForSection( section.kind );
                    const sectionKey = `${ segment }-${ section.kind }-${ section.label }`;
                    // Only the backend-driven lists collapse; the icon-only sidebar has no header to expand them.
                    const isCollapsible = !isCollapsed && !!section.label && segment !== 'client'
                        && ( section.kind === 'company' || section.kind === 'consultant' );
                    const isSectionOpen = !isCollapsible || expandedSections.includes( sectionKey );
                    // Keep the active entity visible even when its section is collapsed
                    const shownEntities = isSectionOpen
                        ? section.entities
                        : section.entities.filter( e => e.id === activeEntityId );

                    // Skip fully-hidden sections outside of customize mode (unless they have actions)
                    if( visibleCount === 0 && !customizing && actions.length === 0 ) return null;

                    return (
                        <div key={ sectionKey } className={ 'consultant-sidebar-section' }>
                            { !isCollapsed && section.label && (
                                <Flexbox
                                    align={ FlexAlign.CENTER }
                                    justify={ FlexJustify.SPACE_BETWEEN }
                                    className={ `consultant-sidebar-section-header ${ isCollapsible ? 'is-collapsible' : '' }` }
                                    onClick={ isCollapsible ? () => toggleSection( sectionKey ) : undefined }
                                >
                                    <span className={ 'consultant-sidebar-section-label' }>
                                        { section.label }
                                        { isCollapsible && ` (${ section.entities.length })` }
                                    </span>
                                    { isCollapsible && (
                                        <button
                                            type={ 'button' }
                                            className={ 'consultant-sidebar-section-header-btn' }
                                            title={ isSectionOpen ? `Collapse ${ section.label }` : `Expand ${ section.label }` }
                                            aria-expanded={ isSectionOpen }
                                            onClick={ ( e: React.MouseEvent ) => {
                                                e.stopPropagation();
                                                toggleSection( sectionKey );
                                            } }
                                        >
                                            <Icon name={ ( isSectionOpen ? 'angle down' : 'angle right' ) as SemanticICONS }/>
                                        </button>
                                    ) }
                                </Flexbox>
                            ) }
                            { shownEntities.length > 0 && (
                                <Menu fluid vertical size={ 'small' } secondary className={ 'sidebar-menu' }>
                                    { shownEntities.map( renderEntity ) }
                                </Menu>
                            ) }
                            { isSectionOpen && actions.length > 0 && (
                                <div className={ 'consultant-sidebar-actions' }>
                                    { actions.map( renderSectionAction ) }
                                </div>
                            ) }
                        </div>
                    );
                } ) }
            </StyledContainer>
        </StyledContainer>
    );
};

export default ConsultantDashSidebar;
